import { computed, type ComputedRef } from "vue";

export interface ImageProps {
  width?: number;
  height?: number;
  aspectRatio?: number;
}

export interface VideoProps {
  width?: number;
  height?: number;
  aspectRatio?: number;
}

export interface MaskProps {
  enabled?: boolean;
  maskAspectRatio?: number;
}

export function 
useMediaContainerLayer(
  mediaType: ComputedRef<"image" | "video" | undefined>,
  objectFit: ComputedRef<"cover" | "contain">,
  imageProps?: ComputedRef<ImageProps | undefined>,
  videoProps?: ComputedRef<VideoProps | undefined>,
  maskProps?: ComputedRef<MaskProps | undefined>,
  hasMask?: ComputedRef<boolean>
) {
  // Хелперы для расчета размеров в зависимости от режима
  const mediaWidth = computed(() => {
    return "100%";
  });

  const mediaHeight = computed(() => {
    return objectFit.value === "cover" ? "100%" : "auto";
  });

  // Вычисляем aspect ratio
  const aspectRatio = computed<number | null>(() => {
    // Если применяется маска, используем aspect ratio маски
    if (hasMask?.value && maskProps?.value?.enabled && maskProps?.value?.maskAspectRatio) {
      return maskProps.value.maskAspectRatio;
    }

    // Иначе используем aspect ratio из изображения или видео
    if (mediaType.value === "image") {
      const explicit = imageProps?.value?.aspectRatio;
      if (explicit && explicit > 0) return explicit;
      const width = imageProps?.value?.width;
      const height = imageProps?.value?.height;
      if (width && height && height !== 0) return width / height;
      // Fallback for contain: 16/9 is more natural than 1:1 for unknown images.
      // For cover the parent defines height so no ratio needed.
      return objectFit.value === "contain" ? 16 / 9 : null;
    } else if (mediaType.value === "video") {
      const explicit = videoProps?.value?.aspectRatio;
      if (explicit && explicit > 0) return explicit;
      const width = videoProps?.value?.width;
      const height = videoProps?.value?.height;
      if (width && height && height !== 0) return width / height;
      return objectFit.value === "contain" ? 16 / 9 : null;
    }
    return null;
  });

  // Классы для object-fit
  const objectFitClasses = computed(() => {
    if (objectFit.value === "cover") {
      return ["w-full", "h-full", "object-cover"];
    }
    return ["w-full", "object-contain"];
  });

  // Стили для object-fit
  const objectFitStyles = computed(() => {
    const styles: Record<string, string | number | undefined> = {
      width: mediaWidth.value,
      height: mediaHeight.value,
      objectFit: objectFit.value,
    };

    if (objectFit.value === "contain" && aspectRatio.value !== null) {
      styles.aspectRatio = aspectRatio.value;
    }

    return styles;
  });

  return {
    mediaWidth,
    mediaHeight,
    aspectRatio,
    objectFitClasses,
    objectFitStyles,
  };
}

